import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  InputAdornment,
  Slider,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Save,
  Refresh,
  Info,
  TrendingUp,
  AccountBalance,
  Warning,
  CheckCircle,
  Person,
  VerifiedUser,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import AdminWrapper from "../../../components/AdminWrapper";
import CancellationPolicyEditor from "./CancellationPolicyEditor";
import {
  GetSystemSettings,
  UpdateCommissionSettings,
  resetSettingsState,
} from "../../../redux/reducer/SystemSettings";

const CommissionSettings = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { settings, loading, error, success } = useSelector(
    (state) => state.systemSettings
  );

  // Store as strings so backspace and clear work naturally in text fields
  const [formData, setFormData] = useState({
    advanceCommission: "0",
    rentCommission: "0",
    lateFee: "0",
    gracePeriod: "5",
    isServiceActive: true,
    ownerCommission: "0",
    propertyVerificationFee: "0",
    otsBankName: "",
    otsAccountTitle: "",
    otsAccountNumber: "",
    otsIban: "",
    // The fee every NEW property is created with — owners only ever see this
    // (disabled) on their property form now, they don't choose it.
    latePolicyType: "PER_DAY_PERCENTAGE",
    latePolicyAmount: "0.5",
    latePolicyMaxPercentage: "10",
    latePolicyGraceDays: "5",
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    dispatch(GetSystemSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings) {
      setFormData({
        advanceCommission: String(settings.advanceCommission ?? 0),
        rentCommission: String(settings.rentCommission ?? 0),
        lateFee: String(settings.lateFee ?? 0),
        gracePeriod: String(settings.gracePeriod ?? 5),
        isServiceActive: settings.isServiceActive ?? true,
        ownerCommission: String(settings.ownerCommission ?? 0),
        propertyVerificationFee: String(settings.propertyVerificationFee ?? 0),
        otsBankName: settings.otsBankName ?? "",
        otsAccountTitle: settings.otsAccountTitle ?? "",
        otsAccountNumber: settings.otsAccountNumber ?? "",
        otsIban: settings.otsIban ?? "",
        latePolicyType: settings.latePaymentPolicy?.type ?? "PER_DAY_PERCENTAGE",
        latePolicyAmount: String(settings.latePaymentPolicy?.amount ?? 0.5),
        latePolicyMaxPercentage: String(settings.latePaymentPolicy?.maximumPercentage ?? 10),
        latePolicyGraceDays: String(settings.latePaymentPolicy?.gracePeriodDays ?? 5),
      });
    }
  }, [settings]);

  useEffect(() => {
    if (success) {
      toast.success("Settings updated successfully!");
      setHasChanges(false);
      dispatch(resetSettingsState());
    }
    if (error) {
      toast.error(error);
      dispatch(resetSettingsState());
    }
  }, [success, error, dispatch]);

  useEffect(() => {
    if (settings) {
      const changed =
        Number(formData.advanceCommission) !== (settings.advanceCommission ?? 0) ||
        Number(formData.rentCommission) !== (settings.rentCommission ?? 0) ||
        Number(formData.lateFee) !== (settings.lateFee ?? 0) ||
        Number(formData.gracePeriod) !== (settings.gracePeriod ?? 5) ||
        formData.isServiceActive !== (settings.isServiceActive ?? true) ||
        Number(formData.ownerCommission) !== (settings.ownerCommission ?? 0) ||
        Number(formData.propertyVerificationFee) !== (settings.propertyVerificationFee ?? 0) ||
        formData.otsBankName !== (settings.otsBankName ?? "") ||
        formData.otsAccountTitle !== (settings.otsAccountTitle ?? "") ||
        formData.otsAccountNumber !== (settings.otsAccountNumber ?? "") ||
        formData.otsIban !== (settings.otsIban ?? "") ||
        formData.latePolicyType !== (settings.latePaymentPolicy?.type ?? "PER_DAY_PERCENTAGE") ||
        Number(formData.latePolicyAmount) !== (settings.latePaymentPolicy?.amount ?? 0.5) ||
        Number(formData.latePolicyMaxPercentage) !== (settings.latePaymentPolicy?.maximumPercentage ?? 10) ||
        Number(formData.latePolicyGraceDays) !== (settings.latePaymentPolicy?.gracePeriodDays ?? 5);
      setHasChanges(changed);
    }
  }, [formData, settings]);

  // Allow free typing including empty string and decimals
  const handleTextChange = (field, value) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Sliders produce numbers directly
  const handleSliderChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: String(value) }));
  };

  // On blur, if field left empty reset to "0"
  const handleBlur = (field) => {
    setFormData((prev) => {
      const val = prev[field];
      if (val === "" || val === ".") {
        return { ...prev, [field]: "0" };
      }
      return prev;
    });
  };

  const handleSave = () => {
    dispatch(
      UpdateCommissionSettings({
        commissionData: {
          advanceCommission: Number(formData.advanceCommission) || 0,
          rentCommission: Number(formData.rentCommission) || 0,
          lateFee: Number(formData.lateFee) || 0,
          gracePeriod: Number(formData.gracePeriod) || 0,
          isServiceActive: formData.isServiceActive,
          ownerCommission: Number(formData.ownerCommission) || 0,
          propertyVerificationFee: Number(formData.propertyVerificationFee) || 0,
          // The server refuses blanks, so only real values are sent.
          ...(formData.otsBankName.trim() && { otsBankName: formData.otsBankName.trim() }),
          ...(formData.otsAccountTitle.trim() && { otsAccountTitle: formData.otsAccountTitle.trim() }),
          ...(formData.otsAccountNumber.trim() && { otsAccountNumber: formData.otsAccountNumber.trim() }),
          ...(formData.otsIban.trim() && { otsIban: formData.otsIban.trim() }),
          latePaymentPolicy: {
            type: formData.latePolicyType,
            amount: Number(formData.latePolicyAmount) || 0,
            gracePeriodDays: Number(formData.latePolicyGraceDays) || 0,
            ...(formData.latePolicyType !== "FIXED_AFTER_GRACE" && {
              maximumPercentage: Number(formData.latePolicyMaxPercentage) || 0,
            }),
          },
        },
      })
    );
  };

  const handleReset = () => {
    if (settings) {
      setFormData({
        advanceCommission: String(settings.advanceCommission ?? 0),
        rentCommission: String(settings.rentCommission ?? 0),
        lateFee: String(settings.lateFee ?? 0),
        gracePeriod: String(settings.gracePeriod ?? 5),
        isServiceActive: settings.isServiceActive ?? true,
        ownerCommission: String(settings.ownerCommission ?? 0),
        propertyVerificationFee: String(settings.propertyVerificationFee ?? 0),
        otsBankName: settings.otsBankName ?? "",
        otsAccountTitle: settings.otsAccountTitle ?? "",
        otsAccountNumber: settings.otsAccountNumber ?? "",
        otsIban: settings.otsIban ?? "",
        latePolicyType: settings.latePaymentPolicy?.type ?? "PER_DAY_PERCENTAGE",
        latePolicyAmount: String(settings.latePaymentPolicy?.amount ?? 0.5),
        latePolicyMaxPercentage: String(settings.latePaymentPolicy?.maximumPercentage ?? 10),
        latePolicyGraceDays: String(settings.latePaymentPolicy?.gracePeriodDays ?? 5),
      });
    }
  };

  const exampleAdvance = 50000;
  const exampleRent = 30000;
  const advanceFee = Math.round(exampleAdvance * ((Number(formData.advanceCommission) || 0) / 100));
  const rentFee = Math.round(exampleRent * ((Number(formData.rentCommission) || 0) / 100));

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b" }}>
              Commission Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure OTS service fees and charges
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleReset}
              disabled={!hasChanges || loading}
              sx={{ borderColor: "#e5e7eb", color: "#64748b" }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={!hasChanges || loading}
              sx={{ bgcolor: "#e94560", "&:hover": { bgcolor: "#d13652" } }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>

        {!formData.isServiceActive && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <strong>Service Fee Disabled:</strong> Commission collection is currently turned off.
            All payments will go directly to landlords without any OTS fee deduction.
          </Alert>
        )}

        {hasChanges && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            You have unsaved changes. Click "Save Changes" to apply them.
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>

              {/* Service Toggle */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Service Fee Collection
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enable or disable commission collection globally
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isServiceActive}
                      onChange={(e) => setFormData((prev) => ({ ...prev, isServiceActive: e.target.checked }))}
                      color="success"
                    />
                  }
                  label={formData.isServiceActive ? "Active" : "Disabled"}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Advance Commission */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <AccountBalance sx={{ color: "#6366f1" }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Advance Payment Commission
                  </Typography>
                  <Tooltip title="Percentage fee charged when a tenant pays the advance or security deposit">
                    <IconButton size="small"><Info sx={{ fontSize: 18, color: "#9ca3af" }} /></IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <Slider
                      value={Number(formData.advanceCommission) || 0}
                      onChange={(e, value) => handleSliderChange("advanceCommission", value)}
                      min={0} max={10} step={0.5}
                      marks={[
                        { value: 0, label: "0%" }, { value: 2.5, label: "2.5%" },
                        { value: 5, label: "5%" }, { value: 7.5, label: "7.5%" }, { value: 10, label: "10%" },
                      ]}
                      valueLabelDisplay="on"
                      valueLabelFormat={(v) => `${v}%`}
                      sx={{ color: "#6366f1", "& .MuiSlider-thumb": { bgcolor: "#6366f1" }, "& .MuiSlider-valueLabel": { bgcolor: "#6366f1" } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth type="text" size="small"
                      value={formData.advanceCommission}
                      onChange={(e) => handleTextChange("advanceCommission", e.target.value)}
                      onBlur={() => handleBlur("advanceCommission")}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Rent Commission */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <TrendingUp sx={{ color: "#22c55e" }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Monthly Rent Commission
                  </Typography>
                  <Tooltip title="Percentage fee charged on each monthly rent payment made by the tenant">
                    <IconButton size="small"><Info sx={{ fontSize: 18, color: "#9ca3af" }} /></IconButton>
                  </Tooltip>
                </Box>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <Slider
                      value={Number(formData.rentCommission) || 0}
                      onChange={(e, value) => handleSliderChange("rentCommission", value)}
                      min={0} max={10} step={0.5}
                      marks={[
                        { value: 0, label: "0%" }, { value: 2.5, label: "2.5%" },
                        { value: 5, label: "5%" }, { value: 7.5, label: "7.5%" }, { value: 10, label: "10%" },
                      ]}
                      valueLabelDisplay="on"
                      valueLabelFormat={(v) => `${v}%`}
                      sx={{ color: "#22c55e", "& .MuiSlider-thumb": { bgcolor: "#22c55e" }, "& .MuiSlider-valueLabel": { bgcolor: "#22c55e" } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth type="text" size="small"
                      value={formData.rentCommission}
                      onChange={(e) => handleTextChange("rentCommission", e.target.value)}
                      onBlur={() => handleBlur("rentCommission")}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Late Payment Settings — legacy rent-collection module only */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Late Payment Settings (legacy rent collection)
              </Typography>
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Late Payment Fee" type="text"
                    value={formData.lateFee}
                    onChange={(e) => handleTextChange("lateFee", e.target.value)}
                    onBlur={() => handleBlur("lateFee")}
                    InputProps={{ startAdornment: <InputAdornment position="start">Rs</InputAdornment> }}
                    helperText="Fixed fee charged after the grace period expires"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Grace Period" type="text"
                    value={formData.gracePeriod}
                    onChange={(e) => handleTextChange("gracePeriod", e.target.value)}
                    onBlur={() => handleBlur("gracePeriod")}
                    InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                    helperText="Number of days after the due date before the late fee applies"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Late Payment Fee Policy — the one that actually matters now:
                  every new property is created with this, and the owner only
                  ever sees it (disabled) on their property form. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Late Payment Fee Policy (applies to every new property)
                </Typography>
                <Tooltip title="This used to be the owner's own choice per property. It's the admin's decision for the whole platform now — the owner's property form only displays it, disabled.">
                  <IconButton size="small"><Info sx={{ fontSize: 18, color: "#9ca3af" }} /></IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose how a late rent payment is penalised — a percentage of rent per day, a
                percentage of rent per month, or a one-time fixed charge. Whatever is saved here
                is what every property is stamped with when the owner (re)saves it.
              </Typography>
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Fee Type</InputLabel>
                    <Select
                      label="Fee Type"
                      value={formData.latePolicyType}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, latePolicyType: e.target.value }))
                      }
                    >
                      <MenuItem value="PER_DAY_PERCENTAGE">% of rent, per day</MenuItem>
                      <MenuItem value="PER_MONTH_PERCENTAGE">% of rent, per month</MenuItem>
                      <MenuItem value="FIXED_AFTER_GRACE">Fixed charge (one-time)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={formData.latePolicyType === "FIXED_AFTER_GRACE" ? "Fee Amount" : "Fee Rate"}
                    type="text"
                    value={formData.latePolicyAmount}
                    onChange={(e) => handleTextChange("latePolicyAmount", e.target.value)}
                    onBlur={() => handleBlur("latePolicyAmount")}
                    InputProps={
                      formData.latePolicyType === "FIXED_AFTER_GRACE"
                        ? { startAdornment: <InputAdornment position="start">Rs</InputAdornment> }
                        : { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                    }
                    helperText={
                      formData.latePolicyType === "PER_DAY_PERCENTAGE"
                        ? "% of monthly rent, charged per day late"
                        : formData.latePolicyType === "PER_MONTH_PERCENTAGE"
                        ? "% of monthly rent, charged per month late"
                        : "Charged once, after the grace period"
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth label="Grace Period" type="text"
                    value={formData.latePolicyGraceDays}
                    onChange={(e) => handleTextChange("latePolicyGraceDays", e.target.value)}
                    onBlur={() => handleBlur("latePolicyGraceDays")}
                    InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                  />
                </Grid>
                {formData.latePolicyType !== "FIXED_AFTER_GRACE" && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth label="Maximum (cap)" type="text"
                      value={formData.latePolicyMaxPercentage}
                      onChange={(e) => handleTextChange("latePolicyMaxPercentage", e.target.value)}
                      onBlur={() => handleBlur("latePolicyMaxPercentage")}
                      InputProps={{ endAdornment: <InputAdornment position="end">% of rent</InputAdornment> }}
                      helperText="The late fee never grows past this, however late the payment"
                    />
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* OTS receiving account (cash bookings) */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                OTS Bank Account
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Shown to tenants who pay the owner in cash, as the account to send the OTS service
                fee to. It is not shown when the fee is 0%.
              </Typography>
              <Grid container spacing={3} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Bank Name"
                    value={formData.otsBankName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, otsBankName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Account Title"
                    value={formData.otsAccountTitle}
                    onChange={(e) => setFormData((prev) => ({ ...prev, otsAccountTitle: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Account Number"
                    value={formData.otsAccountNumber}
                    onChange={(e) => setFormData((prev) => ({ ...prev, otsAccountNumber: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="IBAN"
                    value={formData.otsIban}
                    onChange={(e) => setFormData((prev) => ({ ...prev, otsIban: e.target.value }))}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Owner Commission */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Person sx={{ color: "#f59e0b" }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Owner Commission (Fixed Amount)
                  </Typography>
                  <Tooltip title="Fixed fee charged to the property owner when a rental deal is successfully finalized through OTS">
                    <IconButton size="small"><Info sx={{ fontSize: 18, color: "#9ca3af" }} /></IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  A fixed charge collected from the owner upon successful completion of a rental deal via OTS.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Owner Commission Fee" type="text"
                      value={formData.ownerCommission}
                      onChange={(e) => handleTextChange("ownerCommission", e.target.value)}
                      onBlur={() => handleBlur("ownerCommission")}
                      InputProps={{ startAdornment: <InputAdornment position="start">Rs</InputAdornment> }}
                      helperText="Fixed amount charged to the owner per completed deal"
                      sx={{
                        "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#f59e0b" } },
                        "& label.Mui-focused": { color: "#f59e0b" },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Property Verification Fee */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <VerifiedUser sx={{ color: "#8b5cf6" }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Property Verification Fee (Fixed Amount)
                  </Typography>
                  <Tooltip title="One-time fixed fee charged when a property owner requests official property verification from OTS">
                    <IconButton size="small"><Info sx={{ fontSize: 18, color: "#9ca3af" }} /></IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  A one-time fee charged when an owner requests OTS to officially verify their property listing.
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Verification Fee" type="text"
                      value={formData.propertyVerificationFee}
                      onChange={(e) => handleTextChange("propertyVerificationFee", e.target.value)}
                      onBlur={() => handleBlur("propertyVerificationFee")}
                      InputProps={{ startAdornment: <InputAdornment position="start">Rs</InputAdornment> }}
                      helperText="One-time fee charged for the property verification service"
                      sx={{
                        "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#8b5cf6" } },
                        "& label.Mui-focused": { color: "#8b5cf6" },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

            </Paper>
          </Grid>

          {/* Preview Card */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, borderRadius: 3, bgcolor: "#f8fafc", border: "1px solid #e5e7eb" }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Live Preview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                See how fees will be calculated
              </Typography>

              <Card sx={{ mb: 2, bgcolor: "#eef2ff", border: "1px solid #c7d2fe" }}>
                <CardContent>
                  <Typography variant="subtitle2" color="#4338ca" gutterBottom>
                    Advance Payment Example
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">Advance Amount:</Typography>
                    <Typography variant="body2" fontWeight={600}>Rs {exampleAdvance.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">OTS Fee ({Number(formData.advanceCommission) || 0}%):</Typography>
                    <Typography variant="body2" fontWeight={600} color="error">Rs {advanceFee.toLocaleString()}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" fontWeight={600}>Tenant Pays:</Typography>
                    <Typography variant="body2" fontWeight={700} color="#4338ca">
                      Rs {(exampleAdvance + advanceFee).toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ mb: 2, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                <CardContent>
                  <Typography variant="subtitle2" color="#166534" gutterBottom>
                    Monthly Rent Example
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">Rent Amount:</Typography>
                    <Typography variant="body2" fontWeight={600}>Rs {exampleRent.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2">OTS Fee ({Number(formData.rentCommission) || 0}%):</Typography>
                    <Typography variant="body2" fontWeight={600} color="error">Rs {rentFee.toLocaleString()}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" fontWeight={600}>Tenant Pays:</Typography>
                    <Typography variant="body2" fontWeight={700} color="#166534">
                      Rs {(exampleRent + rentFee).toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ mb: 2, bgcolor: "#fef3c7", border: "1px solid #fcd34d" }}>
                <CardContent>
                  <Typography variant="subtitle2" color="#92400e" gutterBottom>Late Payment</Typography>
                  <Typography variant="body2">
                    After <strong>{Number(formData.gracePeriod) || 0} days</strong> grace period, an additional{" "}
                    <strong>Rs {(Number(formData.lateFee) || 0).toLocaleString()}</strong> will be charged.
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ mb: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Person sx={{ fontSize: 18, color: "#d97706" }} />
                    <Typography variant="subtitle2" color="#92400e" sx={{ mb: 0 }}>Owner Commission</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                    <Typography variant="body2">Fixed Charge per Deal:</Typography>
                    <Typography variant="body2" fontWeight={700} color="#d97706">
                      Rs {(Number(formData.ownerCommission) || 0).toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Charged to the owner upon each completed deal
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ mb: 2, bgcolor: "#f5f3ff", border: "1px solid #ddd6fe" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <VerifiedUser sx={{ fontSize: 18, color: "#7c3aed" }} />
                    <Typography variant="subtitle2" color="#5b21b6" sx={{ mb: 0 }}>Property Verification Fee</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                    <Typography variant="body2">One-Time Fee:</Typography>
                    <Typography variant="body2" fontWeight={700} color="#7c3aed">
                      Rs {(Number(formData.propertyVerificationFee) || 0).toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Charged once when the owner requests property verification
                  </Typography>
                </CardContent>
              </Card>

              <Box sx={{ mt: 1, p: 2, bgcolor: formData.isServiceActive ? "#dcfce7" : "#fee2e2", borderRadius: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {formData.isServiceActive
                    ? <CheckCircle sx={{ color: "#22c55e" }} />
                    : <Warning sx={{ color: "#ef4444" }} />
                  }
                  <Typography variant="subtitle2" color={formData.isServiceActive ? "#166534" : "#dc2626"}>
                    {formData.isServiceActive ? "Fee Collection Active" : "Fee Collection Disabled"}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <CancellationPolicyEditor />

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          theme="colored"
        />
      </Box>
    </>
  );
};

export default CommissionSettings;